import { CHEST_TABLE, COSMETICS, RARITIES, type ChestItem, type ChestSource, type CosmeticKind, type Rarity } from '@/game/chests';
import type { ChestView } from '@/net/api';

import { devArrivalChests, type ArrivalChest } from './arrival-state';

/**
 * Blockari's dev replay roll (screens/ChestScreen.tsx `devRoll`):
 * `common,rare,epic` → start + steps, contents by the final rarity — the
 * same cards per rarity as Blockari's, so the two clips deal alike.
 */
export function devRoll(
  path: string,
  who: { id?: string; source?: ChestSource } = {},
  search = typeof location === 'undefined' ? '' : location.search,
): ChestView {
  const want = path
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is Rarity => (RARITIES as readonly string[]).includes(s));
  const start = want[0] ?? 'common';
  const steps = want.slice(1);
  const final = steps[steps.length - 1] ?? start;
  const c = CHEST_TABLE.contents[final];
  let contents: ChestItem[] = [{ type: 'chain', amount: Math.round((c.chain.min + c.chain.max) / 2) }];
  const rank = RARITIES.indexOf(final);
  if (rank >= 1) contents.push({ type: 'ticket', fee: c.ticket.fee, count: 1 });
  if (rank >= 2) contents.push({ type: 'boost', games: c.boost.games, multiplier: CHEST_TABLE.boostMultiplier });
  if (rank >= 3) contents.push({ type: 'cosmetic', id: 'frame-highroller', kind: 'frame', rarity: 'legendary' });
  contents = devItems(search, final) ?? contents;
  return {
    id: who.id ?? 'dev',
    source: who.source ?? 'rank_up',
    startRarity: start,
    createdAt: new Date(0).toISOString(),
    opened: true,
    steps,
    finalRarity: final,
    contents,
    openedAt: null,
  };
}

/** The dev arrival's chests from a query (`?arrival=<source>&rarity=<r>&steps=…&n=…`), or null. */
export function devArrival(search = typeof location === 'undefined' ? '' : location.search): ArrivalChest[] | null {
  return devArrivalChests(search, (id, source, start, steps) => devRoll([start, ...steps].join(','), { id, source }, search));
}

/**
 * DEV ONLY (`import.meta.env.DEV` is a literal Vite replaces, so a production bundle folds this to null): the exact
 * reward cards a dev chest holds, from `&items=chain,ticket,boost,frame,title`. Each entry may carry a value after a
 * colon (`chain:40`, `ticket:50`, `boost:5`, `frame:frame-neon`, `title:title-wild`) and otherwise takes the
 * chest's own rarity's amount, rung, games or first cosmetic of that kind, so the AAA-card capture
 * (tools/parity/aaa-cards-capture.mjs) can ask for any card type at any rarity through the same `?arrival=` and
 * `?frame=chest` routes a reviewer already uses.
 */
export function devItems(search: string, final: Rarity): ChestItem[] | null {
  if (!import.meta.env.DEV) return null;
  const raw = new URLSearchParams(search).get('items');
  if (raw === null) return null;
  const c = CHEST_TABLE.contents[final];
  const items: ChestItem[] = [];
  for (const part of raw.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean)) {
    const [kind, value] = part.split(':') as [string, string | undefined];
    if (kind === 'chain') items.push({ type: 'chain', amount: Number(value) || Math.round((c.chain.min + c.chain.max) / 2) });
    else if (kind === 'ticket') items.push({ type: 'ticket', fee: Number(value) || c.ticket.fee, count: 1 });
    else if (kind === 'boost') items.push({ type: 'boost', games: Number(value) || c.boost.games, multiplier: CHEST_TABLE.boostMultiplier });
    else if (kind === 'frame' || kind === 'title') {
      const pick = COSMETICS.find((x) => (value ? x.id === value : x.kind === (kind as CosmeticKind) && x.rarity === final));
      if (pick) items.push({ type: 'cosmetic', id: pick.id, kind: pick.kind, rarity: pick.rarity });
    }
  }
  return items.length > 0 ? items : null;
}
